import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { X } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    hearAboutUs: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Label the inboxes so failures are easy to read in toasts
  const inboxes = [
    { label: "Inbox A", access_key: "ebfa40e2-5553-480f-a9cf-6ea189ed8f26" },
    { label: "Inbox B", access_key: "f26921e7-7352-4bfd-9d57-7254032742e8" },
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (value) => {
    setFormData({
      ...formData,
      interest: value
    });
  };

  const handleHearAboutChange = (value) => {
    setFormData({
      ...formData,
      hearAboutUs: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Build payload once per inbox to avoid mutation bugs
    const makePayload = (key) => ({
      access_key: key,
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      interest: formData.interest,
      hear_about_us: formData.hearAboutUs,
      message: formData.message,
    });

    try {
      const results = await Promise.allSettled(
        inboxes.map(async ({ label, access_key }) => {
          const res = await fetch("https://api.web3forms.com/submit", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(makePayload(access_key)),
          });

          // If HTTP not ok, try to read JSON; if fail, synthesize an error
          let json;
          try {
            json = await res.json();
          } catch {
            json = { success: false, message: `Non-JSON response (HTTP ${res.status})` };
          }

          // Ensure consistent shape for both success/failed paths
          return {
            label,
            httpOk: res.ok,
            success: Boolean(json && json.success),
            message: (json && json.message) || `HTTP ${res.status}`,
            raw: json,
          };
        })
      );

      // Normalize the settled results
      const normalized = results.map((r, i) => {
        if (r.status === "fulfilled") return r.value;
        // Rejected fetch/network error
        return {
          label: inboxes[i].label,
          httpOk: false,
          success: false,
          message: r.reason?.message || "Network error",
          raw: null,
        };
      });

      const allSucceeded = normalized.every((r) => r.success === true);

      if (allSucceeded) {
        toast({
          title: "Successfull!",
          description: " We'll respond within 24 hours to get you started on your boxing journey!",

        });

        setFormData({
          name: "",
          email: "",
          phone: "",
          interest: "",
          hearAboutUs: "",
          message: "",
        });

        onClose();
      } else {
        // Build a human-friendly failure summary
        const failed = normalized.filter((r) => !r.success);
        const details = failed
          .map((f) => `${f.label}: ${f.message}`)
          .join(" | ");

        toast({
          title: "Submission issue",
          description: `Not all inboxes received the form. ${details}`,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to submit the form right now. Please try again shortly.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-black border-red-600 text-white max-w-md mx-auto">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle className="text-xl font-bold text-red-600">
            Join Clara Boxing Academy
          </DialogTitle>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="text-white hover:text-red-600 p-1"
          >
            <X size={20} />
          </Button>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <Input
            name="name"
            placeholder="Full Name *"
            value={formData.name}
            onChange={handleInputChange}
            required
            className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-400 focus:border-red-600"
          />

          <Input
            name="email"
            type="email"
            placeholder="Email Address *"
            value={formData.email}
            onChange={handleInputChange}
            required
            className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-400 focus:border-red-600"
          />

          <Input
            name="phone"
            type="tel"
            placeholder="Phone Number"
            value={formData.phone}
            onChange={handleInputChange}
            className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-400 focus:border-red-600"
          />

          <Select value={formData.interest} onValueChange={handleSelectChange}>
            <SelectTrigger className="bg-gray-900 border-gray-700 text-white focus:border-red-600">
              <SelectValue placeholder="What interests you most? *" />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700">
              <SelectItem
                value="Boxing Class (1-on-1)"
                className="text-white hover:bg-red-600"
              >
                Boxing Class (1-on-1)
              </SelectItem>
              <SelectItem
                value="Mitts Session (12 rounds)"
                className="text-white hover:bg-red-600"
              >
                Mitts Session (12 rounds)
              </SelectItem>
              <SelectItem
                value="CBA Monthly Membership (5 days/week)"
                className="text-white hover:bg-red-600"
              >
                CBA Monthly Membership (5 days/week)
              </SelectItem>
            </SelectContent>
          </Select>

          <Select value={formData.hearAboutUs} onValueChange={handleHearAboutChange}>
            <SelectTrigger className="bg-gray-900 border-gray-700 text-white focus:border-red-600">
              <SelectValue placeholder="How did you hear about us?" />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700">
              <SelectItem
                value="Facebook"
                className="text-white hover:bg-red-600"
              >
                Facebook
              </SelectItem>
              <SelectItem
                value="Instagram"
                className="text-white hover:bg-red-600"
              >
                Instagram
              </SelectItem>
              <SelectItem
                value="TikTok"
                className="text-white hover:bg-red-600"
              >
                TikTok
              </SelectItem>
              <SelectItem
                value="Referral from Member"
                className="text-white hover:bg-red-600"
              >
                Referral from Member
              </SelectItem>
              <SelectItem
                value="Family/Friend"
                className="text-white hover:bg-red-600"
              >
                Family/Friend
              </SelectItem>
              <SelectItem
                value="Other"
                className="text-white hover:bg-red-600"
              >
                Other
              </SelectItem>
            </SelectContent>
          </Select>

          <Textarea
            name="message"
            placeholder="Tell us about your goals..."
            value={formData.message}
            onChange={handleInputChange}
            className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-400 focus:border-red-600 min-h-[80px]"
          />

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold"
              disabled={loading}
            >
              {loading ? "Sending..." : "Get Started"}
            </Button>
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              Later
            </Button>
          </div>
        </form>

        <p className="text-xs text-gray-400 text-center mt-4">
          Claim your 3 FREE trial days! 
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;